import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Modal Body Component
 *
 * Wrapper for modal content area with scroll shadow indicators
 *
 * @example
 * // Default (no padding - backwards compatible)
 * <Modal.Body>
 *   <Form style={{ marginTop: '16px', marginBottom: '16px' }}>...</Form>
 * </Modal.Body>
 *
 * @example
 * // With automatic padding (opt-in)
 * <Modal.Body padding="default">
 *   <Form>...</Form>
 * </Modal.Body>
 *
 * @example
 * // No padding for full-width content
 * <Modal.Body padding="none">
 *   <Table />
 * </Modal.Body>
 */
const ModalBody = ({
    children,
    className = '',
    style = {},
    padding = null, // null by default for backwards compatibility
    ...props
}) => {
    const bodyRef = useRef(null);
    const [scrollState, setScrollState] = useState('scrolled-top');

    useEffect(() => {
        const handleScroll = () => {
            const element = bodyRef.current;
            if (!element) return;

            const { scrollTop, scrollHeight, clientHeight } = element;

            // Check if content is scrollable
            const isScrollable = scrollHeight > clientHeight;

            // Find header and footer siblings
            const modalContent = element.parentElement;
            const header = modalContent?.querySelector('.ds-modal-header');
            const footer = modalContent?.querySelector('.ds-modal-footer');

            if (!isScrollable) {
                // Content fits, no shadows needed
                setScrollState('scrolled-none');
                header?.classList.remove('show-shadow');
                footer?.classList.remove('show-shadow');
                return;
            }

            const isAtTop = scrollTop === 0;
            const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;

            // Update scroll state
            if (isAtTop) {
                setScrollState('scrolled-top');
                // At top: hide header shadow, show footer shadow
                header?.classList.remove('show-shadow');
                footer?.classList.add('show-shadow');
            } else if (isAtBottom) {
                setScrollState('scrolled-bottom');
                // At bottom: show header shadow, hide footer shadow
                header?.classList.add('show-shadow');
                footer?.classList.remove('show-shadow');
            } else {
                setScrollState('scrolled-middle');
                // In middle: show both shadows
                header?.classList.add('show-shadow');
                footer?.classList.add('show-shadow');
            }
        };

        const element = bodyRef.current;
        if (element) {
            // Initial check
            handleScroll();

            // Add scroll listener
            element.addEventListener('scroll', handleScroll);

            // Check on resize
            window.addEventListener('resize', handleScroll);

            // Use MutationObserver to detect content changes
            const observer = new MutationObserver(handleScroll);
            observer.observe(element, {
                childList: true,
                subtree: true,
                attributes: true
            });

            return () => {
                element.removeEventListener('scroll', handleScroll);
                window.removeEventListener('resize', handleScroll);
                observer.disconnect();
            };
        }
    }, [children]);

    // Build padding class if padding prop is provided
    // Convert boolean values to string equivalents for class names
    let paddingClass = '';
    if (padding !== null && padding !== undefined) {
        const paddingValue = typeof padding === 'boolean' ? padding.toString() : padding;
        paddingClass = `ds-modal-body--padding-${paddingValue}`;
    }

    return (
        <div
            ref={bodyRef}
            className={`ds-modal-body ${scrollState} ${paddingClass} ${className}`}
            style={style}
            {...props}
        >
            {children}
        </div>
    );
};

ModalBody.propTypes = {
    /** Body content */
    children: PropTypes.node,

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Inline styles */
    style: PropTypes.object,

    /**
     * Padding option for content
     * - null/undefined: No padding (default - backwards compatible)
     * - 'default' or true: Vertical padding (16px top/bottom)
     * - 'none' or false: Explicitly no padding
     * - 'horizontal': Horizontal padding only
     * - 'full': Padding on all sides
     */
    padding: PropTypes.oneOfType([
        PropTypes.oneOf(['default', 'none', 'horizontal', 'full']),
        PropTypes.bool
    ])
};

export default ModalBody;
